import React, { useCallback, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import firebaseApp from '../../../../../firebase-app';
import { useStringsContext } from '../../../../../contexts/strings';
import { Story } from '../../types';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import moment from 'moment';
import BaseDialog, { BaseDialogProps } from '../../../../../components/common/base-dialog';
import StoryMediaCard from './../cards/story-media-card';
import StoryDetailsCard from './../cards/story-details-card';
import { useFormValidate } from '../../../../../hooks/formValidate';
import LoadingButton from '@mui/lab/LoadingButton';
import { getStoryPath } from '../../../../../utils/paths';
import { setDoc, doc, collection } from 'firebase/firestore';
import StoryScheduleCard from './../cards/story-schedule-card';
import { FS_PATH_STORIES } from '../../../../../constants';
import StoryPreviewCard from './../cards/story-preview-card';
import StoryTitle from './../story-title';
import { getStatus } from '../../stories-list';
import DeleteIcon from '@mui/icons-material/Delete';

interface Props extends BaseDialogProps {
  onEditDone: () => void;
  onDelete: () => void;
  selectedStory: Story | null;
  selectedAbbrv: string;
  selectedLeague: string;
}

const StoryEditModal = ({
  open,
  onClose,
  onEditDone,
  onDelete,
  selectedStory,
  selectedAbbrv,
  selectedLeague,
}: Props) => {
  const { strings } = useStringsContext();
  const { enqueueSnackbar } = useSnackbar();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hideValidationError, setHideValidationError] = useState<boolean>(true);
  const [page, setPage] = useState<'edit' | 'preview'>('edit');
  const [updatedSelectedStory, setUpdatedSelectedStory] = useState<Story | null>(null);

  const isEditing = Boolean(selectedStory !== null);
  const pollStatus = isEditing ? getStatus(selectedStory!) : null;

  const storyId = useMemo(() => {
    return selectedStory ? selectedStory.id : doc(collection(firebaseApp.db, FS_PATH_STORIES)).id;
  }, [selectedStory]);

  const { addFormValue, addFormValidation, isValid, isDirty, editedValues } = useFormValidate();

  const createUpdatedSelectedStory = useCallback(() => {
    const newUpdatedSelectedStory = isEditing
      ? { ...selectedStory }
      : ({
          id: storyId,
          abbrv: selectedAbbrv,
          type: 'VIDEO',
          createdAt: moment().toISOString(),
        } satisfies Partial<Story>);

    Object.entries(editedValues).forEach(([key, value]) => {
      newUpdatedSelectedStory[key as keyof Story] = value as any;
    });

    setUpdatedSelectedStory(newUpdatedSelectedStory as Story);
  }, [editedValues, isEditing, selectedAbbrv, selectedStory, storyId]);

  const onSubmit = useCallback(async () => {
    if (!isDirty || !isValid) {
      setHideValidationError(false);
      return;
    }

    if (!updatedSelectedStory) return;

    setIsSubmitting(true);

    try {
      const storyPath = getStoryPath(storyId);
      await setDoc(doc(firebaseApp.db, storyPath), updatedSelectedStory);

      enqueueSnackbar(strings.messages.saveSuccess, {
        variant: 'success',
      });
      onEditDone();
    } catch (error) {
      console.error(error);
      enqueueSnackbar(strings.messages.error, {
        variant: 'error',
      });
    }
  }, [enqueueSnackbar, isDirty, isValid, onEditDone, storyId, strings, updatedSelectedStory]);

  const onNext = useCallback(() => {
    if (!isValid) {
      setHideValidationError(false);
      return;
    }

    if (page === 'edit') {
      createUpdatedSelectedStory();
      setPage('preview');
    } else {
      onSubmit();
    }
  }, [createUpdatedSelectedStory, isValid, onSubmit, page]);

  const onBack = useCallback(() => {
    setPage('edit');
  }, []);

  const onChange = useCallback(
    (key: string, value: string | boolean | null) => {
      addFormValue(key, value);
    },
    [addFormValue]
  );

  const onValidation = useCallback(
    (key: string, isValid: boolean) => {
      addFormValidation(key, isValid);
    },
    [addFormValidation]
  );

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      title={isEditing ? strings.stories.editStory : strings.stories.addStory}
    >
      <DialogContent dividers>
        {page === 'edit' && (
          <Stack spacing={4}>
            {isEditing && selectedStory && (
              <StoryTitle selectedStory={selectedStory} status={pollStatus} />
            )}
            <StoryMediaCard
              selectedStory={selectedStory}
              selectedLeague={selectedLeague}
              onValue={onChange}
              onValidation={onValidation}
              hideValidationError={hideValidationError}
            />
            <StoryDetailsCard
              selectedStory={selectedStory}
              onValue={onChange}
              onValidation={onValidation}
              hideValidationError={hideValidationError}
            />
            <StoryScheduleCard
              selectedStory={selectedStory}
              onValue={onChange}
              onValidation={onValidation}
              hideValidationError={hideValidationError}
            />
            {isEditing && (
              <Button
                startIcon={<DeleteIcon />}
                variant="outlined"
                color="error"
                onClick={onDelete}
              >
                {strings.stories.deleteStory}
              </Button>
            )}
          </Stack>
        )}
        {page === 'preview' && (updatedSelectedStory || selectedStory) && (
          <StoryPreviewCard
            selectedStory={(updatedSelectedStory || selectedStory)!}
            selectedLeague={selectedLeague}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Stack direction="row" flex={1} justifyContent="space-between">
          <Button color="primary" onClick={onClose}>
            {strings.actions.cancel}
          </Button>
          <Stack direction="row" spacing={2}>
            {page === 'preview' && (
              <Button color="primary" onClick={onBack}>
                {strings.actions.back}
              </Button>
            )}
            <LoadingButton
              variant="contained"
              color="primary"
              disabled={!isDirty && !isEditing}
              loading={isSubmitting}
              onClick={onNext}
            >
              {page === 'edit' ? strings.actions.preview : strings.actions.publishChanges}
            </LoadingButton>
          </Stack>
        </Stack>
      </DialogActions>
    </BaseDialog>
  );
};

export default StoryEditModal;
